import { formatMoney } from "@vouch/shared";
import { Check, ShieldCheck, X } from "lucide-react";
import { useState } from "react";
import { PeriodFilter } from "../components/PeriodFilter";
import { Button, Card, Spinner } from "../components/ui";
import { confirm } from "../lib/confirm";
import { useRespondWitness, useWitnessTasks, type Period, type WitnessTask } from "../lib/hooks";
import { withToast } from "../lib/toast";

const STATUS_CHIP: Record<string, string> = {
  pending: "bg-amber-50 text-amber-600",
  confirmed: "bg-green-50 text-green-600",
  declined: "bg-red-50 text-red-600",
};

function fmtDate(s: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export function WitnessPage({ embedded = false }: { embedded?: boolean }): JSX.Element {
  const { data: tasks, isLoading } = useWitnessTasks();
  const respond = useRespondWitness();
  const [period, setPeriod] = useState<Period>({});

  if (isLoading) return <Spinner />;

  const all = tasks ?? [];
  const actionable = all.filter((t) => t.actionable);
  const awaiting = all.filter((t) => t.status === "pending" && !t.actionable);
  let history = all.filter((t) => t.status !== "pending");
  if (period.year) {
    const from = Date.UTC(period.year, period.month ? period.month - 1 : 0, 1);
    const to = Date.UTC(
      !period.month || period.month === 12 ? period.year + 1 : period.year,
      period.month ? period.month % 12 : 0,
      1,
    );
    history = history.filter((t) => {
      const d = new Date(t.respondedAt ?? t.createdAt).getTime();
      return d >= from && d < to;
    });
  }

  async function act(t: WitnessTask, action: "confirm" | "decline"): Promise<void> {
    const ok = await confirm({
      title: action === "confirm" ? "Confirm this loan?" : "Decline to witness?",
      message:
        action === "confirm"
          ? `You're vouching that this loan of ${formatMoney(t.amount, t.currency)} is genuine. The loan activates once all witnesses confirm.`
          : "The person who asked you will be told you declined to witness.",
      confirmLabel: action === "confirm" ? "Yes, confirm" : "Decline",
      danger: action === "decline",
    });
    if (!ok) return;
    await withToast(
      () => respond.mutateAsync({ id: t.id, action }),
      action === "confirm" ? "Confirmed — thanks for vouching" : "Declined",
    );
  }

  return (
    <div className={`${embedded ? "" : "mx-auto max-w-3xl"} space-y-6`}>
      {!embedded && <div className="flex items-center gap-2">
        <ShieldCheck className="text-brand-500" />
        <h1 className="text-2xl font-bold text-brand-500">Witness requests</h1>
      </div>}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
          Needs your confirmation
        </h2>
        {actionable.length === 0 && (
          <Card>
            <p className="text-sm text-slate-500">Nothing waiting on you right now.</p>
          </Card>
        )}
        {actionable.map((t) => (
          <Card key={t.id}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-slate-700">
                  {t.inviterName} asked you to witness a loan
                </p>
                <p className="mt-0.5 text-sm text-slate-500">
                  {t.lenderName ?? "Lender"} → {t.borrowerName ?? "Borrower"} · due {fmtDate(t.dueDate)}
                </p>
                {t.description && <p className="mt-1 text-sm text-slate-500">“{t.description}”</p>}
              </div>
              <p className="text-lg font-semibold text-brand-500">
                {formatMoney(t.amount, t.currency)}
              </p>
            </div>
            <div className="mt-3 flex justify-end gap-2 border-t border-slate-100 pt-3">
              <Button variant="ghost" onClick={() => void act(t, "decline")} disabled={respond.isPending}>
                <X size={16} /> Decline
              </Button>
              <Button onClick={() => void act(t, "confirm")} disabled={respond.isPending}>
                <Check size={16} /> Confirm
              </Button>
            </div>
          </Card>
        ))}
      </section>

      {awaiting.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
            Awaiting the borrower
          </h2>
          {awaiting.map((t) => (
            <Card key={t.id}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-slate-700">{t.inviterName}’s loan</p>
                  <p className="text-sm text-slate-500">
                    You can confirm once the other party accepts.
                  </p>
                </div>
                <p className="font-semibold text-brand-500">{formatMoney(t.amount, t.currency)}</p>
              </div>
            </Card>
          ))}
        </section>
      )}

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">History</h2>
          <PeriodFilter value={period} onChange={setPeriod} />
        </div>
        {history.length === 0 && (
          <Card>
            <p className="text-sm text-slate-500">No past responses in this period.</p>
          </Card>
        )}
        {history.map((t) => (
          <Card key={t.id}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-slate-700">{t.inviterName}’s loan</p>
                <p className="text-sm text-slate-500">
                  {formatMoney(t.amount, t.currency)} · {fmtDate(t.respondedAt)}
                </p>
              </div>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                  STATUS_CHIP[t.status] ?? "bg-slate-100 text-slate-500"
                }`}
              >
                {t.status}
              </span>
            </div>
          </Card>
        ))}
      </section>
    </div>
  );
}
